// @ts-nocheck
/**
 * NetlistBuilder — turn a Velxio circuit (components + wires + board pin
 * state) into a complete ngspice netlist string.
 *
 * Algorithm (see plan phase_8_velxio_implementation §5):
 *   1. Union-Find on wires to identify nets.
 *   2. Canonicalize known-special nets: GND → "0", VCC/VDD/5V/3V3 → "vcc_rail".
 *   3. Auto-name remaining nets "n0", "n1", ... deterministically.
 *   4. Detect floating nodes (no DC path to 0) → add auto pull-down 100 MΩ.
 *   5. Emit component cards via `componentToSpice`.
 *   6. Emit board GPIO source cards (digital or PWM quasi-static).
 *   7. Emit the Vcc rail source.
 *   8. Append `.model` / `.subckt` cards for every used device.
 *   9. Append analysis card (`.op` / `.tran` / `.ac`).
 *  10. `.end`.
 */
import { UnionFind } from "@/lib/velxio/simulation/spice/unionFind";
import { componentToSpice } from "@/lib/velxio/simulation/spice/componentToSpice";
import type {
  BuildNetlistInput,
  BoardForSpice,
  WireForSpice,
} from "@/lib/velxio/simulation/spice/types";
import { getBreadboardConnectionGroups } from "@/lib/velxio/utils/breadboard";

const GROUND_PIN_RE = /^(gnd|vss|vee|ground|gnd\.\d+)$/i;
// Deliberately excludes "V+" / "V-" (which are probe terminals) and
// "VBB" (non-standard). VCC-like pins on boards are handled via the
// board.vccPinNames list, not this regex.
const VCC_PIN_RE = /^(vcc|vdd|vcc_rail|5v|3v3|3\.3v)$/i;

/** metadataId prefixes of components that must NOT be auto-canonicalized
 *  by the pin-name regex (their pins are just probe labels). */
function skipCanonicalization(metadataId: string): boolean {
  return metadataId.startsWith("instr-");
}

/**
 * Sanitize a component / board / pin id for use inside a SPICE identifier
 * (V-source name, etc.). ngspice's interactive `alter` command treats `-`
 * as an arithmetic / range operator and silently no-ops on hyphenated
 * source names — so even though the netlist *parser* tolerates hyphens
 * via the regex on line 206, mid-simulation MCU pin transitions stop
 * propagating after the first solve. Substituting `-` → `_` here keeps
 * the identifier ngspice-safe AND consistent with whatever the scheduler
 * passes to `alter`. Must be reused by MixedModeScheduler when building
 * the alter target name.
 */
export function sanitizeSpiceId(id: string): string {
  return id.replace(/-/g, "_");
}

/**
 * Rail net name for a supply voltage. A board is not a single rail: it may
 * expose 5 V, 3.3 V and VIN pins at once. The board's *nominal* voltage keeps
 * the legacy name "vcc_rail" (so op-amp default power, generic component VCC
 * pins and probes are unchanged), and every other supply voltage gets its own
 * rail net — "vcc_rail_5v0", "vcc_rail_3v3" — driven by its own source. This
 * is why an Arduino's 3.3 V pin no longer reads 5 V and an ESP32's 5 V / VIN
 * pin no longer reads 3.3 V.
 */
function railNetName(voltage: number, dominantVcc: number): string {
  if (Math.abs(voltage - dominantVcc) < 1e-6) return "vcc_rail";
  const tag = voltage.toFixed(1).replace(".", "v"); // 5.0 → "5v0", 3.3 → "3v3"
  return `vcc_rail_${tag}`;
}

/**
 * Human label for a rail net produced by railNetName. Inverse of the "5v0"
 * encoding above, kept next to it so the two can't drift. "vcc_rail" is the
 * board's dominant supply (no voltage in the name); "vcc_rail_5v0" → "5.0 V".
 */
export function railLabel(net: string): string {
  if (net === "vcc_rail") return "the power rail";
  const tag = net.replace(/^vcc_rail_/, "");
  const volts = tag.replace("v", "."); // "5v0" → "5.0"
  return `the ${volts} V rail`;
}

/**
 * Canonicalize a board's supply pins onto per-voltage rail nets. When
 * `railVoltages` is supplied, records each non-nominal rail's voltage so
 * buildNetlist can emit a dedicated source for it.
 */
function canonicalizeVccPins(
  uf: UnionFind,
  board: BoardForSpice,
  keyOf: (id: string, pinName: string) => string,
  dominantVcc: number,
  railVoltages?: Map<string, number>,
): void {
  const voltages = board.vccPinVoltages ?? {};
  for (const pinName of board.vccPinNames ?? []) {
    const v = voltages[pinName] ?? dominantVcc;
    const net = railNetName(v, dominantVcc);
    uf.setCanonical(keyOf(board.id, pinName), net);
    if (railVoltages && net !== "vcc_rail") railVoltages.set(net, v);
  }
}

export interface BuildNetlistResult {
  netlist: string;
  /** "boardId:pinName" → SPICE net name, from the same UF used to build the netlist. */
  pinNetMap: Map<string, string>;
  /**
   * Every SPICE net name in the circuit except canonical "0" (ground).
   * Includes `vcc_rail` plus all auto-named nets (n0, n1, ...).  Used
   * by CircuitSimulationService to ask the solver for every node
   * voltage in one shot (vectorsOfInterest).
   */
  nets: string[];
  /**
   * Every voltage source name in the circuit (without the leading
   * `V` prefix is NOT how ngspice names them — they include the V).
   * Examples: `V_VCC_RAIL`, `V_uno_9`, `V_led1_sense`. Used to
   * request branch currents (`i(v_<name>)`).
   */
  voltageSources: string[];
  /**
   * Supply rails that the wiring merges straight into ground — a hard
   * short (e.g. a wire from the 5 V rail to the GND rail). The union-find
   * resolves the merged net to "0", so the solve itself shows a dead circuit
   * with no current; this list is the only surviving evidence. Consumers
   * (verifier, canvas banner) must surface it loudly.
   */
  railShorts: string[];
  /**
   * Pairs of distinct supply rails wired together (e.g. 5 V tied to 3.3 V).
   * Unlike a rail-to-ground short the solve still converges (one source wins),
   * so nothing else flags it — the verifier surfaces it as a supply short.
   * Each entry is the two rail net names joined by "↔".
   */
  railRailShorts: string[];
  /**
   * Nets with no DC path to a source or ground (they only got a solve value
   * from the auto 100 MΩ pull-down). A part with a pin on one of these isn't
   * in a complete circuit, so no current can flow through it. This is the
   * general "open circuit" signal, derived from topology rather than
   * per-part rules.
   */
  floatingNets: string[];
}

export function buildNetlist(input: BuildNetlistInput): BuildNetlistResult {
  const { components, wires, boards, analysis, extraCards = [] } = input;

  // ── 1. Union-Find over wires ─────────────────────────────────────────────
  const uf = new UnionFind();
  const pinKey = (componentId: string, pinName: string) =>
    `${componentId}:${pinName}`;

  // Seed every pin referenced by a wire (components pins are added on demand).
  // Phase 4: when a wire has `length_cm` set, its endpoints stay in separate
  // nets and a R_wire_<id> card is emitted later (step 7).
  const resistiveWires: typeof wires = [];
  for (const w of wires) {
    const a = pinKey(w.start.componentId, w.start.pinName);
    const b = pinKey(w.end.componentId, w.end.pinName);
    uf.add(a);
    uf.add(b);
    if (w.length_cm !== undefined && w.length_cm > 0) {
      resistiveWires.push(w);
    } else {
      uf.union(a, b);
    }
  }

  for (const comp of components) {
    for (const group of getBreadboardConnectionGroups(comp.metadataId)) {
      const referencedPins = group
        .map((pinName) => pinKey(comp.id, pinName))
        .filter((key) => uf.has(key));
      const [first, ...rest] = referencedPins;
      if (!first) continue;
      uf.add(first);
      for (const key of rest) {
        uf.add(key);
        uf.union(first, key);
      }
    }
  }

  // ── 2. Canonicalize ground / VCC pins ────────────────────────────────────
  const dominantVcc = boards[0]?.vcc ?? 5;
  const railVoltages = new Map<string, number>();
  for (const board of boards) {
    for (const pinName of board.groundPinNames ?? []) {
      uf.setCanonical(pinKey(board.id, pinName), "0");
    }
    canonicalizeVccPins(uf, board, pinKey, dominantVcc, railVoltages);
    // Fallback: any board pin a wire references whose name looks like a
    // ground pin (GND, GND.1, GND.9, etc.) is canonicalized to "0" even if
    // it's not in `groundPinNames`. Boards with many GND pins (ESP32-C3
    // dev kits ship up to 10) often miss some in the per-board list, which
    // would leave wires connected to those pins floating instead of grounded.
    for (const pinName of pinsReferencedByWires(board.id, wires)) {
      if (GROUND_PIN_RE.test(pinName)) {
        uf.setCanonical(pinKey(board.id, pinName), "0");
      } else if (VCC_PIN_RE.test(pinName)) {
        uf.setCanonical(pinKey(board.id, pinName), "vcc_rail");
      }
    }
  }
  for (const comp of components) {
    if (skipCanonicalization(comp.metadataId)) continue;
    for (const pinName of pinsReferencedByWires(comp.id, wires)) {
      if (GROUND_PIN_RE.test(pinName)) {
        uf.setCanonical(pinKey(comp.id, pinName), "0");
      } else if (VCC_PIN_RE.test(pinName)) {
        uf.setCanonical(pinKey(comp.id, pinName), "vcc_rail");
      }
    }
  }

  // ── 3. Auto-name remaining nets deterministically ────────────────────────
  const netNames = assignDeterministicNetNames(uf);

  // Helper: pin → net name (null if pin isn't in any net)
  function netLookup(componentId: string, pinName: string): string | null {
    const key = pinKey(componentId, pinName);
    if (!uf.has(key)) return null;
    return netNames.get(uf.find(key)) ?? null;
  }

  // ── 4. Emit component cards ───────────────────────────────────────────────
  const cards: string[] = [];
  const modelLines = new Set<string>();

  for (const comp of components) {
    const localLookup = (pinName: string) => netLookup(comp.id, pinName);
    const emission = componentToSpice(comp, localLookup, { vcc: dominantVcc });
    if (!emission) continue;
    cards.push(...emission.cards);
    for (const m of emission.modelsUsed) modelLines.add(m);
  }

  // ── 5. Board GPIO sources ─────────────────────────────────────────────────
  // Hyphens are kept in the V-source name (NetlistBuilder regex on line 206
  // captures `[_\w-]*`), so ngspice itself can parse + load the source and
  // expose the branch current as `v_<board>-<pin>#branch` — visible in the
  // canvas voltmeters / branch-current map. BUT ngspice's *interactive*
  // `alter` command treats `-` as an operator and silently no-ops on
  // hyphenated source names — making MCU pin transitions stop propagating
  // after the very first solve. So MixedModeScheduler.onMcuPinChange must
  // build the same sanitized name we emit here. See sanitizeSpiceId().
  for (const board of boards) {
    for (const [pinName, state] of Object.entries(board.pins)) {
      if (state.type === "input") continue; // don't drive the pin
      const net = netLookup(board.id, pinName);
      if (!net) continue;
      if (net === "0" || net.startsWith("vcc_rail")) continue; // already served
      const v = state.type === "digital" ? state.v : state.duty * board.vcc;
      cards.push(
        `V_${sanitizeSpiceId(board.id)}_${sanitizeSpiceId(pinName)} ${net} 0 DC ${v}`,
      );
    }
  }

  // ── 6. Vcc rail source(s) (if any pin referenced them) ────────────────────
  // A board pin normally self-powers its rail with an ideal source. But when
  // the user wires an external supply (battery, regulator VOUT, signal gen)
  // INTO a board power pin, that pin's net is already driven — emitting our
  // own source too would either pin the node to the board's voltage (ignoring
  // the external supply) or, against a regulator's behavioural source, form a
  // voltage-source loop that makes the whole solve fail. So we detect any rail
  // net that another V / B source already drives and let it win instead.
  const externallyDrivenRails = new Set<string>();
  for (const card of cards) {
    const p = card[0];
    const tok = card.split(/\s+/);
    // tok[0] = element name; tok[1] / tok[2] = the two node terminals.
    if (p === "V") {
      // Only a NONZERO voltage source is an external supply. A 0 V source is
      // a current-sense shunt — LEDs and ammeters emit `V_<id>_sense … DC 0`
      // in series with the part, and its terminal often lands ON a rail (an
      // LED plugged straight across the 5 V rail). That shunt merely *touches*
      // the rail, it doesn't drive it, so it must NOT suppress the board's own
      // rail supply — otherwise the rail floats and the part goes dead.
      const dcIdx = tok.findIndex((t) => t.toUpperCase() === "DC");
      const dc = dcIdx >= 0 ? Number.parseFloat(tok[dcIdx + 1]) : Number.NaN;
      if (!Number.isFinite(dc) || dc === 0) continue;
    } else if (p !== "B") {
      continue; // only V (real supply) and B (regulator VOUT) can drive a rail
    }
    for (const net of [tok[1], tok[2]]) {
      if (net?.startsWith("vcc_rail")) externallyDrivenRails.add(net);
    }
  }
  if (hasNet(netNames, "vcc_rail") && !externallyDrivenRails.has("vcc_rail")) {
    cards.unshift(`V_VCC_RAIL vcc_rail 0 DC ${dominantVcc}`);
  }
  // Extra per-voltage supply rails — an Arduino's 3.3 V pin, an ESP32's
  // 5 V / VIN pin, a Pico's VBUS, etc. Each is its own driven node.
  for (const [railNet, voltage] of railVoltages) {
    if (!hasNet(netNames, railNet)) continue;
    if (externallyDrivenRails.has(railNet)) continue;
    cards.unshift(`V_${sanitizeSpiceId(railNet)} ${railNet} 0 DC ${voltage}`);
  }

  // ── 6.5. Wire resistance (Phase 4) ───────────────────────────────────────
  // Wires marked with length_cm get a resistor between their endpoint nets.
  // R = 0.01 ohm/cm — order-of-magnitude correct for AWG 22 copper hookup
  // wire — enough to show voltage drop on long buses without dominating
  // ordinary circuit behaviour.  Emitted before pull-down detection so the
  // resistors count as DC paths between their endpoints.
  for (const w of resistiveWires) {
    const cm = w.length_cm ?? 0;
    if (cm <= 0) continue;
    const ohms = Math.max(0.01, 0.01 * cm);
    const a = netLookup(w.start.componentId, w.start.pinName);
    const b = netLookup(w.end.componentId, w.end.pinName);
    if (!a || !b) continue;
    cards.push(`R_wire_${w.id} ${a} ${b} ${ohms}`);
  }

  // ── 7. Auto pull-downs for floating nets ─────────────────────────────────
  const floating = detectFloatingNets(netNames, cards);
  for (const net of floating) {
    cards.push(`R_autopull_${net} ${net} 0 100Meg`);
  }

  // ── 8. Compose netlist ────────────────────────────────────────────────────
  const lines: string[] = [`* Velxio circuit @ ${new Date().toISOString()}`];
  lines.push(...cards);
  lines.push(...modelLines);
  lines.push(...extraCards);

  switch (analysis.kind) {
    case "op":
      lines.push(".op");
      break;
    case "tran":
      lines.push(`.tran ${analysis.step} ${analysis.stop}`);
      break;
    case "ac": {
      const kind = analysis.type ?? "dec";
      const points = analysis.points ?? 20;
      const fstart = analysis.fstart ?? 1;
      const fstop = analysis.fstop ?? 1e6;
      lines.push(`.ac ${kind} ${points} ${fstart} ${fstop}`);
      break;
    }
  }
  lines.push(".end");

  // ── 9. Build (component|board) pin → net map from the same UF ────────────
  // Every wire endpoint is in the UF. Including component-pin entries (not
  // just board pins) lets the MixedModeScheduler bridge route SPICE voltages
  // to component subscribers downstream of active devices. Legacy ADC
  // injection still works — it only looks up board-prefixed keys.
  const pinNetMap = new Map<string, string>();
  for (const w of wires) {
    for (const endpoint of [w.start, w.end]) {
      const key = pinKey(endpoint.componentId, endpoint.pinName);
      if (!uf.has(key)) continue;
      const netName = netNames.get(uf.find(key));
      if (netName) pinNetMap.set(key, netName);
    }
  }

  // ── 10. Enumerate every net + voltage source for the solve options ───────
  // Distinct, non-ground nets — every node the solver should report.
  const nets = Array.from(new Set(netNames.values())).filter((n) => n !== "0");
  // Voltage sources are any card starting with `V` (uppercase) followed
  // by an underscore or digit.  ngspice's case-insensitive match means
  // both `Vname` and `vname` count.  We emit only uppercase prefixes
  // from componentToSpice + NetlistBuilder, so this regex is safe.
  //
  // The character class MUST include `-` (hyphen). Component ids in
  // examples and user-drawn circuits commonly contain hyphens
  // (`led-builtin`, `led-red`, auto-generated `led-1717…-abc`), and
  // SPICE itself happily parses identifiers with hyphens. If the regex
  // doesn't accept them it truncates the captured name at the first
  // hyphen ⇒ wrong voltageSources entry ⇒ CircuitSimulationService
  // asks ngspice for the WRONG branch-current vector ⇒ branchCurrents
  // lookup returns undefined ⇒ LED brightness stays at zero even though
  // the circuit conducts correctly. Single-character fix, but it
  // unblocks every hyphenated id across every existing project.
  const voltageSources: string[] = [];
  for (const card of cards) {
    const m = card.match(/^([Vv][_\w-]*)\s/);
    if (m) voltageSources.push(m[1]);
  }

  return {
    netlist: lines.join("\n"),
    pinNetMap,
    nets,
    voltageSources,
    railShorts: uf.railShorts(),
    railRailShorts: uf.railRailShorts(),
    floatingNets: [...floating],
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────

function pinsReferencedByWires(
  componentId: string,
  wires: WireForSpice[],
): string[] {
  const pins = new Set<string>();
  for (const w of wires) {
    if (w.start.componentId === componentId) pins.add(w.start.pinName);
    if (w.end.componentId === componentId) pins.add(w.end.pinName);
  }
  return [...pins];
}

function assignDeterministicNetNames(uf: UnionFind): Map<string, string> {
  const reps = [...uf.nets()].sort();
  const out = new Map<string, string>();
  let counter = 0;
  for (const rep of reps) {
    if (rep === "0" || rep.startsWith("vcc_rail")) {
      out.set(rep, rep);
    } else {
      // Strip characters ngspice doesn't like from auto-names
      out.set(rep, `n${counter++}`);
    }
  }
  return out;
}

function hasNet(netNames: Map<string, string>, name: string): boolean {
  for (const v of netNames.values()) if (v === name) return true;
  return false;
}

/** Node terminals of a SPICE card, by element letter (SPICE node-count rules).
 * Everything after them is a value, model name, source reference, or gain —
 * NOT a node — so extracting positionally (instead of "stop at the first token
 * that isn't a known net") lets us walk through internal helper nodes like a
 * battery's `<id>_int` or an ammeter's `<id>_mid`, which are real DC vertices
 * but never appear in `netNames`. */
function nodeTerminals(prefix: string, tokens: string[]): string[] {
  // X (subckt): every token between the instance name and the subckt name.
  if (prefix === "X") return tokens.slice(1, Math.max(1, tokens.length - 1));
  // E/G (controlled sources) and S (switch) list four nodes; the two-terminal
  // elements list two. F/H name a controlling source after their two nodes,
  // which the 2-count skips for free.
  const count = prefix === "E" || prefix === "G" || prefix === "S" ? 4 : 2;
  return tokens.slice(1, 1 + count);
}

/** Whether a card is a source that actively drives the circuit, so its
 * terminals anchor a complete loop the way a rail does. A 0 V sense shunt —
 * the series element LEDs and ammeters emit — carries no drive and must NOT
 * anchor, or every dangling leg downstream of one would look connected. */
function isDrivingSource(prefix: string, tokens: string[]): boolean {
  if (prefix === "B") return true; // behavioural source: always driving
  if (prefix !== "V" && prefix !== "I") return false;
  const dcIdx = tokens.findIndex((t) => t.toUpperCase() === "DC");
  if (dcIdx >= 0) return Number.parseFloat(tokens[dcIdx + 1]) !== 0;
  return true; // SIN / PULSE / AC etc. always drive
}

/**
 * Detect nets that lack a DC path back to a source.
 *
 * Does a graph walk that starts from every circuit anchor — node "0", the
 * supply rails ("vcc_rail…"), and the terminals of every driving source —
 * traversing only DC-conducting cards:
 *   - resistor (R...)
 *   - voltage source (V...)           — V-source is a DC short for connectivity
 *   - current source (I...)
 *   - inductor (L... — treated as DC short)
 *   - switch (S...)
 *   - behavioral source (B...)         — also connects output to ground via its KCL stamp
 *   - MNA-controlled source (E..., G..., F..., H...)
 *   - subckt instance (X...)           — optimistic: assume any X instance exposes DC paths
 *
 * Capacitors (C...) and diodes (D...) are intentionally NOT conductive in this
 * walk — a cap is OPEN at DC and a diode passes no DC on its own, so a net
 * connected onward only through one is still floating.
 *
 * Two things make the walk match real topology. First, node terminals are
 * read positionally (see nodeTerminals) rather than by "stop at the first
 * unknown token", so the internal helper nodes our emitters insert — a
 * battery's `<id>_int`, an ammeter's `<id>_mid`, a relay coil's `<id>_coilmid`
 * — are walked through instead of silently cutting the path. Second, a circuit
 * powered by a battery or bench supply anchors on that source's own terminals:
 * its "+" / "−" pins never canonicalize onto "0" / "vcc_rail", so seeding only
 * from those names would mark every net in a railless-but-sourced loop as
 * floating — which is the false "not part of a complete circuit" this fixes.
 *
 * An older version of this function used a "touched-by-R" heuristic: a net
 * was marked safe if ANY resistor terminal referenced it. That was wrong for
 * topologies like `V0-R1-n0-R2-n1-C-0` where n0 and n1 are chained through
 * R's but neither has a DC path to ground (shipping RC-low-pass example).
 * The graph walk fixes that — it only considers a net safe when a DC path
 * actually traces back to a source anchor.
 *
 * Returns the set of nets that should receive an auto 100 MΩ pull-down.
 */
function detectFloatingNets(
  netNames: Map<string, string>,
  cards: string[],
): Set<string> {
  const nets = new Set(netNames.values());

  // Build an undirected adjacency list over DC-conducting elements. Vertices
  // include internal helper nodes (not in `nets`) — they still carry DC.
  // For a 2-terminal element the two nodes on it become connected.
  // For 3+ terminal (E/G/F/H, S, X) we connect every pair of listed nodes —
  // this is conservative (over-connects), which is the safe side for a
  // "does this net have SOME DC path to a source" question.
  const adj = new Map<string, Set<string>>();
  function ensure(n: string) {
    if (!adj.has(n)) adj.set(n, new Set());
    return adj.get(n)!;
  }
  function link(a: string, b: string) {
    if (a === b) return;
    ensure(a).add(b);
    ensure(b).add(a);
  }

  // Cards that define a DC path between their listed nodes. Capacitor ('C')
  // and diode ('D') are deliberately excluded.
  const DC_PREFIXES = "RLVISBEGFHX";

  // Every net that anchors a complete circuit. Ground is always one; the rails
  // and driving-source terminals are added as we scan the cards.
  const seeds = new Set<string>(["0"]);

  for (const line of cards) {
    const prefix = line[0]?.toUpperCase();
    if (!prefix || DC_PREFIXES.indexOf(prefix) < 0) continue;
    const tokens = line.split(/\s+/);
    const nodes = nodeTerminals(prefix, tokens);
    // Connect every pair of nodes — handles both 2-terminal and N-terminal.
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        link(nodes[i], nodes[j]);
      }
    }
    if (isDrivingSource(prefix, tokens)) {
      for (const n of nodes) seeds.add(n);
    }
  }
  for (const net of nets) if (net.startsWith("vcc_rail")) seeds.add(net);

  // BFS from every anchor.
  const reachable = new Set<string>();
  const queue: string[] = [...seeds];
  while (queue.length) {
    const n = queue.shift()!;
    if (reachable.has(n)) continue;
    reachable.add(n);
    const neigh = adj.get(n);
    if (!neigh) continue;
    for (const m of neigh) if (!reachable.has(m)) queue.push(m);
  }

  const floating = new Set<string>();
  for (const net of nets) {
    if (net === "0" || net.startsWith("vcc_rail")) continue;
    if (!reachable.has(net)) floating.add(net);
  }
  return floating;
}

/**
 * Build a wireId → netName map using the same Union-Find logic as buildNetlist.
 * Lightweight (no SPICE call) — suitable for the overlay to look up voltages.
 */
export function buildWireNetMap(
  input: Pick<BuildNetlistInput, "components" | "wires" | "boards">,
): Map<string, string> {
  const { wires, boards, components } = input;
  const uf = new UnionFind();
  const pin = (cId: string, pName: string) => `${cId}:${pName}`;

  for (const w of wires) {
    const a = pin(w.start.componentId, w.start.pinName);
    const b = pin(w.end.componentId, w.end.pinName);
    uf.add(a);
    uf.add(b);
    uf.union(a, b);
  }

  const dominantVcc = boards[0]?.vcc ?? 5;
  for (const board of boards) {
    for (const pName of board.groundPinNames ?? [])
      uf.setCanonical(pin(board.id, pName), "0");
    canonicalizeVccPins(uf, board, pin, dominantVcc);
  }
  for (const comp of components) {
    if (comp.metadataId.startsWith("instr-")) continue;
    for (const pName of pinsReferencedByWires(comp.id, wires)) {
      if (GROUND_PIN_RE.test(pName)) uf.setCanonical(pin(comp.id, pName), "0");
      else if (VCC_PIN_RE.test(pName))
        uf.setCanonical(pin(comp.id, pName), "vcc_rail");
    }
  }

  const netNames = assignDeterministicNetNames(uf);
  const result = new Map<string, string>();
  for (const w of wires) {
    const key = pin(w.start.componentId, w.start.pinName);
    if (uf.has(key)) {
      const netName = netNames.get(uf.find(key));
      if (netName) result.set(w.id, netName);
    }
  }
  return result;
}

/**
 * Build a map from `"${boardId}:${pinName}"` → SPICE net name for every
 * board pin that participates in the circuit. Used by the ADC injection
 * step in subscribeToStore so it can look up voltages by pin name.
 */
export function buildBoardPinNetMap(
  input: Pick<BuildNetlistInput, "components" | "wires" | "boards">,
): Map<string, string> {
  const { wires, boards, components } = input;
  const uf = new UnionFind();
  const pin = (cId: string, pName: string) => `${cId}:${pName}`;

  // Collect board IDs for fast lookup
  const boardIds = new Set(boards.map((b) => b.id));

  for (const w of wires) {
    const a = pin(w.start.componentId, w.start.pinName);
    const b = pin(w.end.componentId, w.end.pinName);
    uf.add(a);
    uf.add(b);
    uf.union(a, b);
  }

  // Canonicalize board ground/vcc pins (from boardPinGroups metadata)
  const dominantVcc = boards[0]?.vcc ?? 5;
  for (const board of boards) {
    for (const pName of board.groundPinNames ?? []) {
      const k = pin(board.id, pName);
      uf.add(k);
      uf.setCanonical(k, "0");
    }
    for (const pName of board.vccPinNames ?? []) uf.add(pin(board.id, pName));
    canonicalizeVccPins(uf, board, pin, dominantVcc);
  }
  // Canonicalize non-board component GND/VCC pins referenced by wires
  for (const comp of components) {
    if (comp.metadataId.startsWith("instr-")) continue;
    if (boardIds.has(comp.id)) continue; // board handled above
    for (const pName of pinsReferencedByWires(comp.id, wires)) {
      if (GROUND_PIN_RE.test(pName)) uf.setCanonical(pin(comp.id, pName), "0");
      else if (VCC_PIN_RE.test(pName))
        uf.setCanonical(pin(comp.id, pName), "vcc_rail");
    }
  }

  const netNames = assignDeterministicNetNames(uf);
  const result = new Map<string, string>();

  // For each board, collect ALL pins that appear in wires (via wire endpoints)
  // plus the explicit groundPinNames/vccPinNames/pins lists.
  for (const board of boards) {
    const wireReferencedPins = pinsReferencedByWires(board.id, wires);
    const allPins = new Set([
      ...(board.groundPinNames ?? []),
      ...(board.vccPinNames ?? []),
      ...Object.keys(board.pins ?? {}),
      ...wireReferencedPins, // ← the pins that actually exist in the UF
    ]);
    for (const pName of allPins) {
      const k = pin(board.id, pName);
      if (uf.has(k)) {
        const netName = netNames.get(uf.find(k));
        if (netName) result.set(k, netName);
      }
    }
  }
  return result;
}

/** Re-export types for callers. */
export type {
  BuildNetlistInput,
  ComponentForSpice,
  BoardForSpice,
  WireForSpice,
} from "@/lib/velxio/simulation/spice/types";
